import type { LoginChallenge, MfaFactor, UserLoginData } from '@/shared/types/authTypes';

const EMAIL_ONLY_CHALLENGE: LoginChallenge = {
  methods: ['EMAIL_OTP'],
  preferred: 'EMAIL_OTP',
  emailOtp: { sent: false, expiresIn: null },
  allowEmailFallback: false,
  allowRecovery: false,
};

const isMfaFactor = (value: unknown): value is MfaFactor =>
  value === 'EMAIL_OTP' || value === 'TOTP' || value === 'RECOVERY';

export const formatTotpManualKey = (key: string): string =>
  key.replace(/\s+/g, '').toUpperCase().replace(/(.{4})/g, '$1 ').trim();

export const resolveLoginChallenge = (login: UserLoginData | null | undefined): LoginChallenge | null => {
  if (!login?.authId) {
    return null;
  }

  if (!login.challenge) {
    return EMAIL_ONLY_CHALLENGE;
  }

  const methods = (login.challenge.methods ?? []).filter(isMfaFactor);
  const preferred = isMfaFactor(login.challenge.preferred)
    ? login.challenge.preferred
    : methods[0] ?? 'EMAIL_OTP';
  const resolvedMethods = methods.length > 0 ? methods : [preferred];

  return {
    methods: resolvedMethods,
    preferred,
    emailOtp: login.challenge.emailOtp ?? (preferred === 'EMAIL_OTP' ? { sent: false, expiresIn: null } : null),
    allowEmailFallback: login.challenge.allowEmailFallback ?? resolvedMethods.includes('TOTP'),
    allowRecovery: login.challenge.allowRecovery ?? resolvedMethods.includes('TOTP'),
  };
};

const hasTotp = (challenge: LoginChallenge): boolean =>
  challenge.preferred === 'TOTP' || challenge.methods.includes('TOTP');

export const needsEmailOtpSend = (challenge: LoginChallenge): boolean => {
  const hasEmail = challenge.preferred === 'EMAIL_OTP' || challenge.methods.includes('EMAIL_OTP');
  return hasEmail && !hasTotp(challenge) && challenge.emailOtp?.sent !== true;
};

export const canStartLoginChallenge = (challenge: LoginChallenge): boolean => {
  if (hasTotp(challenge)) {
    return true;
  }
  return challenge.emailOtp?.sent === true;
};

export const withEmailOtpSent = (challenge: LoginChallenge, expiresIn?: number | null): LoginChallenge => ({
  ...challenge,
  emailOtp: { sent: true, expiresIn: expiresIn ?? challenge.emailOtp?.expiresIn ?? null },
});
